import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Send, Eye, Trash2, Upload, Users, Mail, BarChart3, Save, Search, Play, Filter, Zap, Activity, AlertTriangle, CheckCircle2, HelpCircle, Copy, LayoutTemplate, Blocks, ArrowUpDown, UserMinus, RotateCcw, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Btn, Badge } from "../components/common";
import { Modal, Field, Selecter, TextArea, fmtDT, fmtD } from "./crm/crmui";
import PlatformSelector from "../components/PlatformSelector";
import { useAutoRefresh } from "../lib/autoRefresh";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: BarChart3 },
  { key: "health", label: "Saude", icon: Activity },
  { key: "campaigns", label: "Campanhas", icon: Mail },
  { key: "automations", label: "Automations", icon: Zap },
  { key: "lists", label: "Listas", icon: Users },
  { key: "segments", label: "Segmentos", icon: Filter },
  { key: "contacts", label: "Contactos", icon: Users },
  { key: "unsubscribes", label: "Removidos GDPR", icon: UserMinus },
];

const CAMPAIGN_STATES = { rascunho: "bg-slate-100 text-slate-700", agendada: "bg-amber-100 text-amber-700", enviada: "bg-emerald-100 text-emerald-700" };
const CONTACT_STATES = { ativo: "bg-emerald-100 text-emerald-700", unsubscribed: "bg-slate-100 text-slate-500", bounced: "bg-red-100 text-red-700", complaint: "bg-orange-100 text-orange-700" };
const PLATAFORMAS = ["", "Cognietica", "Privaxia", "Assiduo", "Recrutai"];

const STARTER_HTML = `<h2>Ola {{nome}}</h2>
<p>Esperamos que este email o(a) encontre bem.</p>
<p>Descubra as novidades da {{plataforma}}...</p>
<p><a href="https://cogniseg.com">Saber mais</a></p>
<p style="font-size:11px;color:#94a3b8">Se nao quer receber estes emails, <a href="{{unsubscribe_url}}">cancele aqui</a>.</p>`;

export default function Marketing() {
  const nav = useNavigate();
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [campaignTemplates, setCampaignTemplates] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [analytics, setAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [senders, setSenders] = useState([]);
  const [segments, setSegments] = useState([]);
  const [editSegment, setEditSegment] = useState(null);
  const [segPreview, setSegPreview] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [editAutomation, setEditAutomation] = useState(null);
  const [enrollFor, setEnrollFor] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthDays, setHealthDays] = useState(30);
  const [selectedList, setSelectedList] = useState("");
  const [searchContact, setSearchContact] = useState("");

  // Modais
  useEffect(() => {
    if (!showTemplatePicker) return;
    api.get("/marketing/campaign-templates").then(({ data }) => setCampaignTemplates(data || [])).catch(() => setCampaignTemplates([]));
  }, [showTemplatePicker]);
  const [editCampaign, setEditCampaign] = useState(null);
  const [editList, setEditList] = useState(null);
  const [editContact, setEditContact] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importFile, setImportFile] = useState({ open: false, list_mode: "existing", list_id: "", new_list_name: "", tags: "", file: null });
  const [busy, setBusy] = useState(false);
  const [historyModal, setHistoryModal] = useState(null);

  const load = async () => {
    try {
      const [a, c, l, sd, seg, au, hh] = await Promise.all([
        api.get("/marketing/analytics/overview"),
        api.get("/marketing/campaigns"),
        api.get("/marketing/lists"),
        api.get("/marketing/senders"),
        api.get("/marketing/segments"),
        api.get("/marketing/automations"),
        api.get(`/marketing/health/senders?days=${healthDays}`),
      ]);
      setAnalytics(a.data); setCampaigns(c.data || []); setLists(l.data || []);
      setSenders(sd.data || []);
      setSegments(seg.data || []); setAutomations(au.data || []);
      setHealth(hh.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  const loadContacts = async () => {
    try {
      const params = {};
      if (selectedList) params.list_id = selectedList;
      if (searchContact) params.q = searchContact;
      const { data } = await api.get("/marketing/contacts", { params });
      setContacts(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { if (tab === "contacts") loadContacts(); }, [tab, selectedList, searchContact]); // eslint-disable-line

  const [unsubs, setUnsubs] = useState([]);
  const [unsubEvidence, setUnsubEvidence] = useState(null);
  const loadUnsubscribes = async () => {
    try {
      const { data } = await api.get("/marketing/unsubscribes?limit=500");
      setUnsubs(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { if (tab === "unsubscribes") loadUnsubscribes(); }, [tab]);

  const openEvidence = async (contactId) => {
    try {
      const { data } = await api.get(`/marketing/unsubscribes/${contactId}/evidence`);
      setUnsubEvidence(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reactivateUnsub = async (contactId) => {
    const motivo = window.prompt("Motivo da reactivacao (obrigatorio - fica registado como evidencia):");
    if (!motivo || !motivo.trim()) return;
    try {
      await api.post(`/marketing/unsubscribes/${contactId}/reativar`, { motivo });
      toast.success("Contacto reactivado.");
      loadUnsubscribes();
      setUnsubEvidence(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const salvarCampanha = async () => {
    if (!editCampaign.nome.trim() || !editCampaign.assunto.trim()) return toast.error("Nome e assunto sao obrigatorios.");
    setBusy(true);
    try {
      if (editCampaign.id) await api.put(`/marketing/campaigns/${editCampaign.id}`, editCampaign);
      else await api.post("/marketing/campaigns", editCampaign);
      toast.success("Guardada."); setEditCampaign(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const previewCampanha = async (c) => {
    try {
      const { data } = await api.post(`/marketing/campaigns/${c.id}/preview`);
      setPreview({ campaign: c, data });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const enviarCampanha = async (c) => {
    if (!confirm(`Enviar campanha "${c.nome}"? Esta acao e definitiva.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/campaigns/${c.id}/send`);
      toast.success(`Enviados: ${data.enviados}/${data.total}${data.erros ? ` (${data.erros} erros)` : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const eliminarCampanha = async (c) => {
    if (!confirm(`Eliminar "${c.nome}"?`)) return;
    try { await api.delete(`/marketing/campaigns/${c.id}`); toast.success("Eliminada."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const duplicarCampanha = async (c) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/campaigns/${c.id}/duplicate`);
      toast.success(`Duplicada como "${data.nome}". Aberta no editor.`);
      await load();
      nav(`/app/marketing/editor/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const salvarLista = async () => {
    if (!editList.nome.trim()) return toast.error("Indique nome.");
    try {
      if (editList.id) await api.put(`/marketing/lists/${editList.id}`, editList);
      else await api.post("/marketing/lists", editList);
      toast.success("Guardada."); setEditList(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const eliminarLista = async (l) => {
    if (!confirm(`Eliminar lista "${l.nome}"?`)) return;
    try { await api.delete(`/marketing/lists/${l.id}`); toast.success("Eliminada."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const salvarContacto = async () => {
    if (!editContact.email.trim()) return toast.error("Indique email.");
    try {
      if (editContact.id) await api.put(`/marketing/contacts/${editContact.id}`, editContact);
      else await api.post("/marketing/contacts", editContact);
      toast.success("Guardado."); setEditContact(null); loadContacts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doImport = async () => {
    if (!importFile.file) return toast.error("Selecione o CSV.");
    const fd = new FormData();
    fd.append("file", importFile.file);
    const params = new URLSearchParams();
    // Se o utilizador escolheu criar nova lista, envia new_list_name; senao list_id.
    if (importFile.list_mode === "new" && importFile.new_list_name?.trim()) {
      params.set("new_list_name", importFile.new_list_name.trim());
    } else if (importFile.list_id) {
      params.set("list_id", importFile.list_id);
    }
    if (importFile.tags) params.set("tags_csv", importFile.tags);
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/contacts/import-csv?${params.toString()}`, fd);
      const extra = data.lista_criada ? ` (lista '${data.lista_criada.nome}' criada)` : "";
      toast.success(`Import: novos=${data.novos}, atualizados=${data.atualizados}, erros=${data.erros}${extra}`);
      setImportFile({ open: false, list_id: "", list_mode: "existing", new_list_name: "", tags: "", file: null });
      loadContacts(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // --- Segments ---
  const previewSegmento = async (filter) => {
    try {
      const { data } = await api.post("/marketing/segments/preview", { nome: "tmp", filter });
      setSegPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const salvarSegmento = async () => {
    if (!editSegment.nome?.trim()) return toast.error("Indique nome do segmento.");
    try {
      if (editSegment.id) await api.put(`/marketing/segments/${editSegment.id}`, editSegment);
      else await api.post("/marketing/segments", editSegment);
      toast.success("Segmento guardado.");
      setEditSegment(null); setSegPreview(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const eliminarSegmento = async (s) => {
    if (!confirm(`Eliminar segmento "${s.nome}"?`)) return;
    try { await api.delete(`/marketing/segments/${s.id}`); toast.success("Eliminado."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // --- Automations ---
  const salvarAutomation = async () => {
    if (!editAutomation.nome?.trim()) return toast.error("Indique nome.");
    if (!editAutomation.steps?.length) return toast.error("Adicione pelo menos 1 passo.");
    try {
      if (editAutomation.id) await api.put(`/marketing/automations/${editAutomation.id}`, editAutomation);
      else await api.post("/marketing/automations", editAutomation);
      toast.success("Automation guardada.");
      setEditAutomation(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const eliminarAutomation = async (a) => {
    if (!confirm(`Eliminar automation "${a.nome}"? Runs em curso serao apagados.`)) return;
    try { await api.delete(`/marketing/automations/${a.id}`); toast.success("Eliminada."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const enrolarManual = async () => {
    if (!enrollFor?.contact_ids?.length) return toast.error("Selecione contactos.");
    try {
      const { data } = await api.post(`/marketing/automations/${enrollFor.automation.id}/enroll`,
                                      { contact_ids: enrollFor.contact_ids });
      toast.success(`Enrolados: ${data.enrolled} - saltados: ${data.skipped}`);
      setEnrollFor(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const dispararTick = async () => {
    try { const { data } = await api.post("/marketing/automations/tick"); toast.success(`Processados: ${data.processados}, enviados: ${data.enviados}, saidas: ${data.saidas}, concluidos: ${data.concluidos}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const showHistory = async (c) => {
    try {
      const { data } = await api.get(`/marketing/contacts/${c.id}/history`);
      setHistoryModal({ contact: c, sends: data });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <>
      <PageHeader overline="Comunicacoes" title="Email Marketing" />

      <div className="flex gap-1 mb-4 border-b border-tint">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`mkt-tab-${t.key}`}
            className={`text-xs px-4 py-2 border-b-2 inline-flex items-center gap-1.5 -mb-px ${tab === t.key ? "border-blue text-navy font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && analytics && (
        <MarketingDashboard analytics={analytics} campaigns={campaigns} nav={nav} />
      )}

      {tab === "campaigns" && (
        <div>
          <div className="flex flex-wrap items-center gap-2 justify-end mb-3">
            <Btn variant="ghost" onClick={() => setShowTemplatePicker(true)} testid="new-from-template">
              <LayoutTemplate size={14} /> Criar da template...
            </Btn>
            <Btn variant="ghost" onClick={() => nav("/app/marketing/editor")} testid="new-editor">
              <Blocks size={14} /> Nova (editor visual)
            </Btn>
            <Btn onClick={() => setEditCampaign({ nome: "", plataforma: "", servico: "", assunto: "", corpo_html: STARTER_HTML, list_ids: [], segment_ids: [], tags_include: [], tags_exclude: [], sender_id: "", sender_email: "", sender_name: "", track_opens: true, track_clicks: true, partner_code: "" })} testid="new-campaign">
              <Plus size={14} /> Nova (rapida)
            </Btn>
          </div>
          <div className="grid gap-2" data-testid="mkt-campaigns">
            {campaigns.length === 0 && <div className="text-center text-muted2 border border-tint rounded-lg py-12">Sem campanhas.</div>}
            {campaigns.map((c) => (
              <div key={c.id} data-testid={`campaign-${c.id}`} className="border border-tint rounded-lg p-3 bg-white flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-navy truncate">{c.nome}</div>
                  <div className="text-[11px] text-muted2">{c.plataforma || "-"} - {c.assunto}</div>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-slate-500">
                  <span>{c.stats?.enviados || 0} env</span>
                  <span className="text-blue">{c.stats?.open_rate || 0}% abrir</span>
                  <span className="text-emerald-600">{c.stats?.click_rate || 0}% clique</span>
                </div>
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${CAMPAIGN_STATES[c.estado] || "bg-slate-100"}`}>{c.estado}</span>
                <button onClick={() => previewCampanha(c)} title="Preview destinatarios" className="p-1.5 text-slate-500 hover:text-blue"><Eye size={13} /></button>
                <button onClick={() => nav(`/app/marketing/editor/${c.id}`)} title="Abrir no editor visual" data-testid={`campaign-open-editor-${c.id}`} className="text-[11px] px-2 py-1 rounded border border-blue text-blue hover:bg-blue/5 inline-flex items-center gap-1">
                  <Blocks size={11} /> Editor
                </button>
                {c.estado === "rascunho" && (
                  <>
                    <button onClick={() => setEditCampaign({ ...c })} title="Editar (form rapido)" className="text-[11px] px-2 py-1 rounded border border-tint">Editar</button>
                    <button onClick={() => enviarCampanha(c)} title="Enviar" disabled={busy} data-testid={`campaign-send-${c.id}`}
                      className="text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"><Send size={11} /> Enviar</button>
                  </>
                )}
                <button onClick={() => duplicarCampanha(c)} title="Duplicar (cria copia em rascunho)" disabled={busy}
                  data-testid={`campaign-duplicate-${c.id}`}
                  className="text-[11px] px-2 py-1 rounded border border-tint hover:bg-slate-50 inline-flex items-center gap-1">
                  <Copy size={11} /> Duplicar
                </button>
                <button onClick={() => eliminarCampanha(c)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "lists" && (
        <div>
          <div className="flex justify-end mb-3">
            <Btn onClick={() => setEditList({ nome: "", descricao: "", plataforma: "", tags: [] })} testid="new-list"><Plus size={14} /> Nova lista</Btn>
          </div>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {lists.length === 0 && <div className="col-span-full text-center text-muted2 border border-tint rounded-lg py-12">Sem listas.</div>}
            {lists.map((l) => (
              <div key={l.id} data-testid={`list-${l.id}`} className="border border-tint rounded-lg p-3 bg-white">
                <div className="font-semibold text-navy">{l.nome}</div>
                <div className="text-[11px] text-muted2">{l.plataforma || "generico"}</div>
                <div className="text-2xl font-bold text-navy mt-2">{l.n_contactos}</div>
                <div className="text-[10px] uppercase text-muted2">contactos ativos</div>
                <div className="flex gap-1 mt-2">
                  <button onClick={() => { setSelectedList(l.id); setTab("contacts"); }} data-testid={`list-view-contacts-${l.id}`}
                    className="text-[11px] px-2 py-1 rounded border border-blue/30 text-blue hover:bg-blue/5 flex items-center gap-1">
                    <Users size={11} /> Ver contactos
                  </button>
                  <button onClick={() => setEditList({ ...l })} className="text-[11px] px-2 py-1 rounded bg-blue text-white">Editar</button>
                  <button onClick={() => eliminarLista(l)} className="text-[11px] px-2 py-1 rounded border border-red-200 text-red-600"><Trash2 size={11} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "contacts" && (
        <div>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={searchContact} onChange={(e) => setSearchContact(e.target.value)}
                placeholder="Pesquisar por email/nome/empresa" data-testid="contact-search"
                className="w-full pl-8 pr-3 py-2 text-sm border border-tint rounded" />
            </div>
            <select value={selectedList} onChange={(e) => setSelectedList(e.target.value)}
              className="text-sm border border-tint rounded px-2" data-testid="contact-filter-list">
              <option value="">Todas as listas</option>
              {lists.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
            </select>
            <Btn variant="ghost" onClick={() => setImportFile({ open: true, list_mode: "existing", list_id: "", new_list_name: "", tags: "", file: null })} testid="contacts-import"><Upload size={14} /> Import CSV</Btn>
            <Btn onClick={() => setEditContact({ email: "", nome: "", empresa: "", telefone: "", tags: [], list_ids: [], consentimento: true })} testid="new-contact"><Plus size={14} /> Novo</Btn>
          </div>
          <div className="space-y-1" data-testid="mkt-contacts">
            {contacts.length === 0 && <div className="text-center text-muted2 border border-tint rounded-lg py-12">Sem contactos.</div>}
            {contacts.map((c) => (
              <div key={c.id} data-testid={`contact-${c.id}`} className="border border-tint rounded-md p-2 bg-white flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-navy text-sm truncate">{c.nome || c.email}</div>
                  <div className="text-[11px] text-muted2 truncate">{c.email} {c.empresa && `- ${c.empresa}`}</div>
                </div>
                <div className="flex gap-0.5">{(c.tags || []).slice(0, 3).map((t) => <span key={t} className="text-[9px] bg-slate-100 px-1.5 py-0.5 rounded">{t}</span>)}</div>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${CONTACT_STATES[c.estado] || "bg-slate-100"}`}>{c.estado}</span>
                <button onClick={() => showHistory(c)} title="Historico de comunicacoes" data-testid={`contact-history-${c.id}`} className="p-1 text-slate-500 hover:text-blue"><Mail size={13} /></button>
                <button onClick={() => setEditContact({ ...c })} className="text-[11px] px-2 py-1 rounded border border-tint">Editar</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "health" && (
        <HealthPanel data={health} days={healthDays}
          onDaysChange={(d) => { setHealthDays(d); api.get(`/marketing/health/senders?days=${d}`).then((r) => setHealth(r.data)).catch(() => {}); }}
          onRefresh={load} />
      )}

      {tab === "segments" && (
        <div>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="text-xs text-muted2 max-w-2xl">
              Segmentos dinamicos aplicam filtros AND/OR sobre tags, atributos, listas e historico (aberturas/cliques). Usa-se dentro de campanhas para complementar as listas.
            </div>
            <Btn onClick={() => setEditSegment({ nome: "", plataforma: "", descricao: "", filter: { op: "and", conditions: [] } })} testid="new-segment"><Plus size={14} /> Novo segmento</Btn>
          </div>
          <div className="grid gap-2 md:grid-cols-2" data-testid="mkt-segments">
            {segments.length === 0 && <div className="col-span-full text-center text-muted2 border border-tint rounded-lg py-12">Sem segmentos.</div>}
            {segments.map((s) => (
              <div key={s.id} data-testid={`segment-${s.id}`} className="border border-tint rounded-lg p-3 bg-white">
                <div className="flex items-start justify-between mb-1">
                  <div className="min-w-0">
                    <div className="font-semibold text-navy truncate">{s.nome}</div>
                    <div className="text-[11px] text-muted2 truncate">{(s.plataformas && s.plataformas.length > 0 ? s.plataformas.join(", ") : (s.plataforma || "generico"))} - {s.filter?.conditions?.length || 0} condicoes ({s.filter?.op || "and"})</div>
                  </div>
                  <span className="text-[9px] font-bold bg-blue text-white px-1.5 py-0.5 rounded">{s.n_contactos || 0}</span>
                </div>
                <div className="flex gap-1 mt-2">
                  <button onClick={() => setEditSegment({ ...s })} className="text-[11px] px-2 py-1 rounded bg-blue text-white" data-testid={`segment-edit-${s.id}`}>Editar</button>
                  <button onClick={() => eliminarSegmento(s)} className="text-[11px] px-2 py-1 rounded border border-red-200 text-red-600" data-testid={`segment-del-${s.id}`}><Trash2 size={11} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "automations" && (
        <div>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="text-xs text-muted2 max-w-2xl">
              Drip campaigns por remetente. Cada passo espera N horas antes de disparar. Condicoes globais de saida param o fluxo quando o contacto abre, clica, converte, unsubscribe ou bounces.
            </div>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={dispararTick} testid="autos-tick"><Play size={13} /> Tick manual</Btn>
              <Btn onClick={() => setEditAutomation({ nome: "", plataforma: "", sender_id: "", ativo: true, trigger: { type: "manual", value: "" }, exit_conditions: ["unsubscribed", "bounced"], steps: [{ assunto: "", corpo_html: STARTER_HTML, delay_hours: 0, track_opens: true, track_clicks: true }] })} testid="new-automation"><Plus size={14} /> Nova automation</Btn>
            </div>
          </div>
          <div className="grid gap-2 md:grid-cols-2" data-testid="mkt-automations">
            {automations.length === 0 && <div className="col-span-full text-center text-muted2 border border-tint rounded-lg py-12">Sem automations. Crie uma para cada fluxo de onboarding/nurture.</div>}
            {automations.map((a) => (
              <div key={a.id} data-testid={`automation-${a.id}`} className="border border-tint rounded-lg p-3 bg-white">
                <div className="flex items-start justify-between mb-1">
                  <div className="min-w-0">
                    <div className="font-semibold text-navy truncate flex items-center gap-2">
                      {a.nome}
                      {!a.ativo && <span className="text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">inativa</span>}
                    </div>
                    <div className="text-[11px] text-muted2">{a.plataforma || "generico"} - trigger: {a.trigger?.type}{a.trigger?.value ? ` (${a.trigger.value})` : ""}</div>
                    <div className="text-[10px] text-slate-400">{a.steps?.length || 0} steps - saidas: {(a.exit_conditions || []).join(", ") || "-"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-navy">{a.n_ativos || 0}</div>
                    <div className="text-[9px] uppercase text-muted2">em curso</div>
                  </div>
                </div>
                <div className="flex gap-1 mt-2 flex-wrap">
                  <button onClick={() => setEditAutomation({ ...a })} className="text-[11px] px-2 py-1 rounded bg-blue text-white" data-testid={`auto-edit-${a.id}`}>Editar</button>
                  <button onClick={() => setEnrollFor({ automation: a, contact_ids: [] })} className="text-[11px] px-2 py-1 rounded border border-tint" data-testid={`auto-enroll-${a.id}`}><Users size={11} className="inline" /> Enrolar</button>
                  <button onClick={() => eliminarAutomation(a)} className="text-[11px] px-2 py-1 rounded border border-red-200 text-red-600" data-testid={`auto-del-${a.id}`}><Trash2 size={11} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "unsubscribes" && (
        <div className="space-y-3">
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-900" data-testid="gdpr-info">
            <b>Lista Removidos GDPR</b> - contactos que exerceram o direito de oposicao ao marketing. Reactivacao apenas com evidencia (novo consentimento). Registamos: data, motivo, IP e user-agent.
          </div>
          <div className="border border-tint rounded-lg bg-white overflow-hidden" data-testid="unsubscribes-table">
            <table className="w-full text-[13px]">
              <thead className="bg-tint text-navy">
                <tr>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Nome</th>
                  <th className="text-left px-3 py-2">Empresa</th>
                  <th className="text-left px-3 py-2">Motivo</th>
                  <th className="text-left px-3 py-2">Data</th>
                  <th className="text-left px-3 py-2 w-40">Accoes</th>
                </tr>
              </thead>
              <tbody>
                {unsubs.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-4 text-center text-muted2">Nenhum contacto removido.</td></tr>
                )}
                {unsubs.map((c) => (
                  <tr key={c.id} className="border-t border-tint" data-testid={`unsub-row-${c.id}`}>
                    <td className="px-3 py-2 font-mono text-[12px]">{c.email}</td>
                    <td className="px-3 py-2">{c.nome || "-"}</td>
                    <td className="px-3 py-2">{c.empresa || "-"}</td>
                    <td className="px-3 py-2 text-[11px]">{c.unsubscribe_motivo || (c.estado === "spam_reported" ? "Reportado como Spam" : "Cancelamento pelo titular")}</td>
                    <td className="px-3 py-2 text-[11px] text-muted2">{fmtDT(c.unsubscribe_em)}</td>
                    <td className="px-3 py-2 text-[11px]">
                      <button onClick={() => openEvidence(c.id)}
                        data-testid={`unsub-evidence-${c.id}`}
                        className="px-2 py-1 rounded border border-tint hover:bg-tint mr-1">
                        <FileText size={11} className="inline" /> Evidencia
                      </button>
                      <button onClick={() => reactivateUnsub(c.id)}
                        data-testid={`unsub-reativar-${c.id}`}
                        className="px-2 py-1 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                        <RotateCcw size={11} className="inline" /> Reactivar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modais */}
      <Modal open={!!editCampaign} onClose={() => setEditCampaign(null)} title={editCampaign?.id ? "Editar campanha" : "Nova campanha"} testid="campaign-modal" size="xl">
        {editCampaign && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Nome" value={editCampaign.nome} onChange={(v) => setEditCampaign({ ...editCampaign, nome: v })} testid="camp-nome" />
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Plataforma</label>
                <PlatformSelector value={editCampaign.plataforma || ""} onChange={(v) => setEditCampaign({ ...editCampaign, plataforma: v })}
                  testid="camp-plat" placeholder="- Generico -" />
              </div>
              <Field label="Servico" value={editCampaign.servico} onChange={(v) => setEditCampaign({ ...editCampaign, servico: v })} testid="camp-servico" />
            </div>
            <Field label="Assunto" value={editCampaign.assunto} onChange={(v) => setEditCampaign({ ...editCampaign, assunto: v })} testid="camp-assunto" />
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">Remetente</label>
              <select value={editCampaign.sender_id || ""} onChange={(e) => setEditCampaign({ ...editCampaign, sender_id: e.target.value })}
                data-testid="camp-sender"
                className="w-full px-3 py-2 border border-tint rounded text-sm">
                <option value="">- Automatico (por plataforma / predefinido) -</option>
                {senders.filter((s) => s.ativo).map((s) => (
                  <option key={s.id} value={s.id} disabled={!s.sendgrid_api_key_configurado}>
                    {s.nome} - {s.from_email}{s.plataforma ? ` (${s.plataforma})` : ""}{!s.sendgrid_api_key_configurado ? " - sem key" : ""}
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-muted2 mt-1">
                Se nao escolher, o sistema usa o remetente da plataforma da campanha ou o predefinido.
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">Listas destinatarias</label>
              <div className="border border-tint rounded p-2 max-h-32 overflow-y-auto space-y-1">
                {lists.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={(editCampaign.list_ids || []).includes(l.id)}
                      onChange={(e) => setEditCampaign({ ...editCampaign, list_ids: e.target.checked ? [...(editCampaign.list_ids || []), l.id] : (editCampaign.list_ids || []).filter((x) => x !== l.id) })} />
                    {l.nome} <span className="text-slate-400 text-[11px]">({l.n_contactos})</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">Segmentos dinamicos (opcional, uniao com listas)</label>
              <div className="border border-tint rounded p-2 max-h-24 overflow-y-auto space-y-1" data-testid="camp-segments">
                {segments.length === 0 && <div className="text-xs text-muted2">Sem segmentos. Crie em Segmentos.</div>}
                {segments.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={(editCampaign.segment_ids || []).includes(s.id)}
                      onChange={(e) => setEditCampaign({ ...editCampaign, segment_ids: e.target.checked ? [...(editCampaign.segment_ids || []), s.id] : (editCampaign.segment_ids || []).filter((x) => x !== s.id) })} />
                    {s.nome} <span className="text-slate-400 text-[11px]">({s.n_contactos})</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tags incluir (virgula)" value={(editCampaign.tags_include || []).join(",")} onChange={(v) => setEditCampaign({ ...editCampaign, tags_include: v.split(",").map((x) => x.trim()).filter(Boolean) })} testid="camp-tags-inc" />
              <Field label="Tags excluir (virgula)" value={(editCampaign.tags_exclude || []).join(",")} onChange={(v) => setEditCampaign({ ...editCampaign, tags_exclude: v.split(",").map((x) => x.trim()).filter(Boolean) })} testid="camp-tags-exc" />
            </div>
            <Field label="Codigo de parceiro (opcional)" value={editCampaign.partner_code || ""} onChange={(v) => setEditCampaign({ ...editCampaign, partner_code: v })} testid="camp-partner" />
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">Corpo HTML (variaveis: {"{{nome}}, {{email}}, {{empresa}}, {{plataforma}}, {{unsubscribe_url}}"})</label>
              <textarea value={editCampaign.corpo_html} onChange={(e) => setEditCampaign({ ...editCampaign, corpo_html: e.target.value })}
                data-testid="camp-html" rows={10} className="w-full px-3 py-2 border border-tint rounded text-xs font-mono" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setEditCampaign(null)}>Cancelar</Btn>
              <Btn onClick={salvarCampanha} disabled={busy} testid="camp-save"><Save size={14} /> Guardar</Btn>
            </div>
          </>
        )}
      </Modal>

      <Modal open={!!editList} onClose={() => setEditList(null)} title={editList?.id ? "Editar lista" : "Nova lista"} testid="list-modal">
        {editList && (
          <>
            <Field label="Nome" value={editList.nome} onChange={(v) => setEditList({ ...editList, nome: v })} testid="list-nome" />
            <TextArea label="Descricao" value={editList.descricao} onChange={(v) => setEditList({ ...editList, descricao: v })} testid="list-desc" />
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">Plataforma</label>
              <PlatformSelector value={editList.plataforma || ""} onChange={(v) => setEditList({ ...editList, plataforma: v })}
                testid="list-plat" placeholder="- Generico -" />
            </div>
            <Field label="Tags (virgula)" value={(editList.tags || []).join(",")} onChange={(v) => setEditList({ ...editList, tags: v.split(",").map((x) => x.trim()).filter(Boolean) })} testid="list-tags" />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setEditList(null)}>Cancelar</Btn>
              <Btn onClick={salvarLista} testid="list-save"><Save size={14} /> Guardar</Btn>
            </div>
          </>
        )}
      </Modal>

      <Modal open={showTemplatePicker} onClose={() => setShowTemplatePicker(false)} title="Escolher template Cogniseg" testid="template-picker-modal" size="xl">
        <div className="grid md:grid-cols-2 gap-3" data-testid="template-picker-grid">
          {campaignTemplates.length === 0 && <div className="col-span-2 text-center text-muted2 py-6">A carregar templates...</div>}
          {campaignTemplates.map((t) => (
            <button key={t.id} data-testid={`template-pick-${t.id}`}
              onClick={() => { setShowTemplatePicker(false); nav(`/app/marketing/editor?template=${t.id}`); }}
              className="text-left border border-tint rounded-lg p-3 bg-white hover:border-blue hover:shadow-sm transition-all">
              <div className="flex items-center gap-2 mb-1">
                <LayoutTemplate size={14} className="text-blue" />
                <span className="font-semibold text-navy">{t.nome}</span>
              </div>
              <div className="text-[11px] text-muted2 mb-2">{t.categoria}</div>
              <div className="text-[12px] text-slate-600">{t.descricao}</div>
              <div className="text-[10px] text-slate-500 mt-2">{(t.blocks || []).length} blocos</div>
            </button>
          ))}
        </div>
      </Modal>

      <Modal open={!!editContact} onClose={() => setEditContact(null)} title={editContact?.id ? "Editar contacto" : "Novo contacto"} testid="contact-modal">
        {editContact && (
          <>
            <Field label="Email" value={editContact.email} onChange={(v) => setEditContact({ ...editContact, email: v })} testid="ct-email" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome" value={editContact.nome} onChange={(v) => setEditContact({ ...editContact, nome: v })} testid="ct-nome" />
              <Field label="Empresa" value={editContact.empresa} onChange={(v) => setEditContact({ ...editContact, empresa: v })} testid="ct-emp" />
            </div>
            <Field label="Telefone" value={editContact.telefone} onChange={(v) => setEditContact({ ...editContact, telefone: v })} testid="ct-tel" />
            <Field label="Tags (virgula)" value={(editContact.tags || []).join(",")} onChange={(v) => setEditContact({ ...editContact, tags: v.split(",").map((x) => x.trim()).filter(Boolean) })} testid="ct-tags" />
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">Listas</label>
              <div className="border border-tint rounded p-2 max-h-32 overflow-y-auto space-y-1">
                {lists.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={(editContact.list_ids || []).includes(l.id)}
                      onChange={(e) => setEditContact({ ...editContact, list_ids: e.target.checked ? [...(editContact.list_ids || []), l.id] : (editContact.list_ids || []).filter((x) => x !== l.id) })} />
                    {l.nome}
                  </label>
                ))}
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editContact.consentimento} onChange={(e) => setEditContact({ ...editContact, consentimento: e.target.checked })} /> Consentimento RGPD</label>
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setEditContact(null)}>Cancelar</Btn>
              <Btn onClick={salvarContacto} testid="ct-save"><Save size={14} /> Guardar</Btn>
            </div>
          </>
        )}
      </Modal>

      <Modal open={importFile.open} onClose={() => setImportFile({ open: false, list_mode: "existing", list_id: "", new_list_name: "", tags: "", file: null })} title="Importar CSV" testid="import-modal">
        <p className="text-xs text-muted2">Colunas: email (obrig.), nome, empresa, telefone.</p>
        <div>
          <label className="block text-xs uppercase text-muted2 mb-1">Lista destino</label>
          <div className="flex gap-3 text-sm mb-2">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={importFile.list_mode === "existing"}
                onChange={() => setImportFile({ ...importFile, list_mode: "existing", new_list_name: "" })}
                data-testid="imp-mode-existing" />
              Escolher existente
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={importFile.list_mode === "new"}
                onChange={() => setImportFile({ ...importFile, list_mode: "new", list_id: "" })}
                data-testid="imp-mode-new" />
              Criar nova lista
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={importFile.list_mode === "none"}
                onChange={() => setImportFile({ ...importFile, list_mode: "none", list_id: "", new_list_name: "" })}
                data-testid="imp-mode-none" />
              Sem lista
            </label>
          </div>
          {importFile.list_mode === "existing" && (
            <Selecter label="" value={importFile.list_id} onChange={(v) => setImportFile({ ...importFile, list_id: v })}
              options={[{ value: "", label: "-- Selecionar lista --" }, ...lists.map((l) => ({ value: l.id, label: l.nome }))]} testid="imp-list" />
          )}
          {importFile.list_mode === "new" && (
            <Field label="" value={importFile.new_list_name} onChange={(v) => setImportFile({ ...importFile, new_list_name: v })}
              placeholder="Nome da nova lista (ex: Newsletter Q4 2026)" testid="imp-new-list-name" />
          )}
        </div>
        <Field label="Tags a aplicar (virgula)" value={importFile.tags} onChange={(v) => setImportFile({ ...importFile, tags: v })} testid="imp-tags" />
        <input type="file" accept=".csv" onChange={(e) => setImportFile({ ...importFile, file: e.target.files?.[0] })} data-testid="imp-file"
          className="w-full text-sm border border-tint rounded p-2" />
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="ghost" onClick={() => setImportFile({ open: false, list_mode: "existing", list_id: "", new_list_name: "", tags: "", file: null })}>Cancelar</Btn>
          <Btn onClick={doImport} disabled={busy || !importFile.file || (importFile.list_mode === "new" && !importFile.new_list_name?.trim())} testid="imp-save"><Upload size={14} /> Importar</Btn>
        </div>
      </Modal>

      <Modal open={!!preview} onClose={() => setPreview(null)} title="Preview destinatarios" testid="preview-modal">
        {preview && (
          <>
            <div className="text-sm mb-2">Campanha: <strong>{preview.campaign.nome}</strong></div>
            <div className="text-2xl font-bold text-navy">{preview.data.n_destinatarios}</div>
            <div className="text-[10px] uppercase text-muted2">destinatarios</div>
            <div className="mt-3 text-xs">
              <div className="uppercase text-muted2 text-[10px] mb-1">Amostra</div>
              {preview.data.sample.map((s, i) => <div key={i} className="border-t border-tint py-1">{s.nome || "-"} - {s.email}</div>)}
            </div>
          </>
        )}
      </Modal>

      <Modal open={!!historyModal} onClose={() => setHistoryModal(null)} title={`Historico - ${historyModal?.contact?.email || ""}`} testid="history-modal" size="lg">
        {historyModal && (
          <>
            {historyModal.sends.length === 0 && <div className="text-muted2 text-center py-6">Sem historico.</div>}
            <div className="space-y-1">
              {historyModal.sends.map((s) => (
                <div key={s.id} className="border border-tint rounded p-2 text-xs">
                  <div className="font-semibold text-navy">{s.campanha?.nome || "?"}</div>
                  <div className="text-slate-500">{s.campanha?.assunto} - {s.sent_at?.slice(0, 16)}</div>
                  <div className="flex gap-3 mt-1 text-[11px]">
                    <span>Estado: <strong>{s.estado}</strong></span>
                    <span>Aberturas: {s.opened_at?.length || 0}</span>
                    <span>Cliques: {s.clicked_at?.length || 0}</span>
                    {s.bounced && <span className="text-red-600">bounced</span>}
                    {s.unsubscribed && <span className="text-amber-600">unsubscribed</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>
      <Modal open={!!editSegment} onClose={() => { setEditSegment(null); setSegPreview(null); }} title={editSegment?.id ? "Editar segmento" : "Novo segmento"} testid="segment-modal" size="lg">
        {editSegment && (
          <SegmentEditor segment={editSegment} setSegment={setEditSegment} lists={lists}
            preview={segPreview} onPreview={previewSegmento} onSave={salvarSegmento} onCancel={() => { setEditSegment(null); setSegPreview(null); }} />
        )}
      </Modal>

      <Modal open={!!editAutomation} onClose={() => setEditAutomation(null)} title={editAutomation?.id ? "Editar automation" : "Nova automation"} testid="automation-modal" size="xl">
        {editAutomation && (
          <AutomationEditor automation={editAutomation} setAutomation={setEditAutomation}
            senders={senders} lists={lists} onSave={salvarAutomation} onCancel={() => setEditAutomation(null)} />
        )}
      </Modal>

      <Modal open={!!enrollFor} onClose={() => setEnrollFor(null)} title={`Enrolar em: ${enrollFor?.automation?.nome || ""}`} testid="enroll-modal" size="lg">
        {enrollFor && (
          <>
            <div className="text-xs text-muted2 mb-2">Selecione contactos ativos a enrolar (contactos ja em curso sao ignorados).</div>
            <ContactPicker lists={lists} value={enrollFor.contact_ids}
              onChange={(ids) => setEnrollFor({ ...enrollFor, contact_ids: ids })} />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setEnrollFor(null)}>Cancelar</Btn>
              <Btn onClick={enrolarManual} testid="enroll-save"><Save size={14} /> Enrolar {enrollFor.contact_ids.length}</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* Evidencia GDPR do unsubscribe */}
      <Modal open={!!unsubEvidence} onClose={() => setUnsubEvidence(null)} title="Evidencia de cancelamento" testid="unsub-evidence-modal">
        {unsubEvidence && (
          <div className="space-y-3">
            <div className="text-sm">
              <b>Contacto:</b> <span className="font-mono text-[12px]">{unsubEvidence.contact?.email}</span>
              {unsubEvidence.contact?.nome && <span className="text-muted2"> ({unsubEvidence.contact.nome})</span>}
            </div>
            <div className="border border-tint rounded p-2 text-[11px] bg-slate-50">
              <div><b>Estado actual:</b> {unsubEvidence.contact?.estado}</div>
              <div><b>Motivo:</b> {unsubEvidence.contact?.unsubscribe_motivo || "-"}</div>
              <div><b>Data:</b> {unsubEvidence.contact?.unsubscribe_em || "-"}</div>
              <div><b>IP:</b> <span className="font-mono">{unsubEvidence.contact?.unsubscribe_ip || "-"}</span></div>
              <div><b>User-Agent:</b> <span className="font-mono text-[10px]">{unsubEvidence.contact?.unsubscribe_user_agent || "-"}</span></div>
              {unsubEvidence.contact?.reativado_em && (
                <>
                  <hr className="my-2 border-tint"/>
                  <div className="text-emerald-700"><b>Reactivado:</b> {unsubEvidence.contact.reativado_em}</div>
                  <div><b>Por:</b> {unsubEvidence.contact.reativado_por}</div>
                  <div><b>Motivo reactivacao:</b> {unsubEvidence.contact.reativado_motivo}</div>
                </>
              )}
            </div>
            {unsubEvidence.evidencias?.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-navy mb-1">Historico ({unsubEvidence.evidencias.length})</div>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {unsubEvidence.evidencias.map((e) => (
                    <div key={e.id} className="text-[11px] border border-tint rounded p-2 bg-white">
                      <div className="flex items-center justify-between">
                        <span className="text-navy font-semibold">{e.tipo === "reativacao" ? "Reactivacao" : "Cancelamento"}</span>
                        <span className="text-muted2">{fmtDT(e.em, true)}</span>
                      </div>
                      <div className="text-muted2">{e.motivo || "-"}</div>
                      {e.ip && <div className="text-[10px] font-mono text-muted2">IP: {e.ip}</div>}
                      {e.por && <div className="text-[10px] text-muted2">Por: {e.por}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setUnsubEvidence(null)}>Fechar</Btn>
              {unsubEvidence.contact?.estado !== "ativo" && (
                <Btn onClick={() => reactivateUnsub(unsubEvidence.contact.id)} testid="evidence-reactivate">
                  <RotateCcw size={12} /> Reactivar contacto
                </Btn>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

// ============================================================
// Sub-componentes: editor de segmento, automation e picker
// ============================================================
const FIELDS = [
  { value: "tag", label: "Tag" },
  { value: "list_id", label: "Lista" },
  { value: "estado", label: "Estado" },
  { value: "email_domain", label: "Dominio do email" },
  { value: "empresa", label: "Empresa (contains)" },
  { value: "consentimento", label: "Consentimento" },
  { value: "opened_campaign", label: "Abriu campanha" },
  { value: "clicked_campaign", label: "Clicou campanha" },
];
const OPS = [
  { value: "eq", label: "igual a" },
  { value: "neq", label: "diferente de" },
  { value: "in", label: "esta em" },
  { value: "not_in", label: "nao esta em" },
  { value: "contains", label: "contem" },
];

function SegmentEditor({ segment, setSegment, lists, preview, onPreview, onSave, onCancel }) {
  const conditions = segment.filter?.conditions || [];
  const updFilter = (partial) => setSegment({ ...segment, filter: { ...(segment.filter || {}), ...partial } });
  const updCond = (i, patch) => {
    const next = [...conditions];
    next[i] = { ...next[i], ...patch };
    updFilter({ conditions: next });
  };
  const addCond = () => updFilter({ conditions: [...conditions, { field: "tag", operator: "eq", value: "" }] });
  const rmCond = (i) => updFilter({ conditions: conditions.filter((_, idx) => idx !== i) });

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nome" value={segment.nome} onChange={(v) => setSegment({ ...segment, nome: v })} testid="seg-nome" />
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Plataformas (opcional, multi-seleccao)</label>
          <PlatformSelector multi value={segment.plataformas || (segment.plataforma ? [segment.plataforma] : [])}
            onChange={(v) => setSegment({ ...segment, plataformas: v, plataforma: (v || [])[0] || "" })}
            testid="seg-plat" placeholder="- Qualquer plataforma -" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <label className="text-sm font-semibold text-navy">Operador logico:</label>
        <select value={segment.filter?.op || "and"} onChange={(e) => updFilter({ op: e.target.value })}
          data-testid="seg-op" className="text-sm border border-tint rounded px-2 py-1">
          <option value="and">AND (todas)</option>
          <option value="or">OR (qualquer)</option>
        </select>
      </div>
      <div className="space-y-2 border border-tint rounded-lg p-3 bg-slate-50/50" data-testid="seg-conditions">
        {conditions.length === 0 && <div className="text-center text-muted2 text-xs py-3">Sem condicoes. Adicione uma para comecar.</div>}
        {conditions.map((c, i) => (
          <div key={i} className="grid grid-cols-12 gap-1 items-center" data-testid={`seg-cond-${i}`}>
            <select value={c.field} onChange={(e) => updCond(i, { field: e.target.value })} className="col-span-4 text-xs border border-tint rounded px-2 py-1.5 bg-white">
              {FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <select value={c.operator} onChange={(e) => updCond(i, { operator: e.target.value })} className="col-span-3 text-xs border border-tint rounded px-2 py-1.5 bg-white">
              {OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {c.field === "list_id" ? (
              <select value={c.value || ""} onChange={(e) => updCond(i, { value: e.target.value })} className="col-span-4 text-xs border border-tint rounded px-2 py-1.5 bg-white">
                <option value="">-</option>
                {lists.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
              </select>
            ) : (
              <input value={c.value || ""} onChange={(e) => updCond(i, { value: e.target.value })} placeholder="valor"
                className="col-span-4 text-xs border border-tint rounded px-2 py-1.5" />
            )}
            <button onClick={() => rmCond(i)} className="col-span-1 text-red-500 p-1 rounded hover:bg-red-50"><Trash2 size={12} /></button>
          </div>
        ))}
        <button onClick={addCond} className="text-xs text-blue font-semibold" data-testid="seg-addcond">+ Adicionar condicao</button>
      </div>
      {preview && (
        <div className="text-xs bg-tint rounded p-2" data-testid="seg-preview">
          <b>{preview.n_contactos}</b> contactos correspondem. Amostra: {preview.sample?.slice(0, 5).map((s) => s.email).join(", ") || "-"}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Btn variant="ghost" onClick={onCancel}>Cancelar</Btn>
        <Btn variant="ghost" onClick={() => onPreview(segment.filter)} testid="seg-preview-btn"><Eye size={13} /> Preview</Btn>
        <Btn onClick={onSave} testid="seg-save"><Save size={14} /> Guardar</Btn>
      </div>
    </>
  );
}

const TRIGGER_TYPES = [
  { value: "manual", label: "Manual (backoffice)" },
  { value: "list_join", label: "Entrada em lista" },
  { value: "tag_added", label: "Tag adicionada" },
  { value: "lead_marketing", label: "Conversao em lead marketing" },
];
const EXITS = [
  { value: "opened", label: "Abriu qualquer email" },
  { value: "clicked", label: "Clicou qualquer link" },
  { value: "converted_lead", label: "Converteu em lead CRM" },
  { value: "unsubscribed", label: "Cancelou subscricao" },
  { value: "bounced", label: "Bounced" },
];

function AutomationEditor({ automation, setAutomation, senders, lists, onSave, onCancel }) {
  const upd = (k, v) => setAutomation({ ...automation, [k]: v });
  const updStep = (i, patch) => {
    const next = [...(automation.steps || [])];
    next[i] = { ...next[i], ...patch };
    upd("steps", next);
  };
  const addStep = () => upd("steps", [...(automation.steps || []), { assunto: "", corpo_html: "", delay_hours: 24, track_opens: true, track_clicks: true }]);
  const rmStep = (i) => upd("steps", (automation.steps || []).filter((_, idx) => idx !== i));
  const toggleExit = (v) => {
    const cur = new Set(automation.exit_conditions || []);
    if (cur.has(v)) cur.delete(v); else cur.add(v);
    upd("exit_conditions", Array.from(cur));
  };
  const triggerNeedsValue = automation.trigger?.type === "list_join" || automation.trigger?.type === "tag_added";

  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Nome" value={automation.nome || ""} onChange={(v) => upd("nome", v)} testid="auto-nome" />
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Plataforma</label>
          <PlatformSelector value={automation.plataforma || ""} onChange={(v) => upd("plataforma", v)}
            testid="auto-plat" placeholder="- Generico -" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Remetente</label>
          <select value={automation.sender_id || ""} onChange={(e) => upd("sender_id", e.target.value)}
            data-testid="auto-sender" className="w-full px-3 py-2 border border-tint rounded text-sm">
            <option value="">- Auto (por plataforma / predef.) -</option>
            {senders.filter((s) => s.ativo && s.sendgrid_api_key_configurado).map((s) => (
              <option key={s.id} value={s.id}>{s.nome} - {s.from_email}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Trigger de entrada</label>
          <select value={automation.trigger?.type || "manual"} onChange={(e) => upd("trigger", { ...(automation.trigger || {}), type: e.target.value })}
            data-testid="auto-trigger-type" className="w-full px-3 py-2 border border-tint rounded text-sm">
            {TRIGGER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        {triggerNeedsValue && (
          automation.trigger?.type === "list_join" ? (
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">Lista alvo</label>
              <select value={automation.trigger?.value || ""} onChange={(e) => upd("trigger", { ...(automation.trigger || {}), value: e.target.value })}
                data-testid="auto-trigger-list" className="w-full px-3 py-2 border border-tint rounded text-sm">
                <option value="">-</option>
                {lists.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
              </select>
            </div>
          ) : (
            <Field label="Tag alvo" value={automation.trigger?.value || ""} onChange={(v) => upd("trigger", { ...(automation.trigger || {}), value: v })} testid="auto-trigger-tag" />
          )
        )}
      </div>
      <div>
        <label className="block text-sm font-semibold text-navy mb-1">Condicoes globais de saida</label>
        <div className="flex flex-wrap gap-2" data-testid="auto-exits">
          {EXITS.map((e) => {
            const on = (automation.exit_conditions || []).includes(e.value);
            return (
              <button key={e.value} onClick={() => toggleExit(e.value)} data-testid={`auto-exit-${e.value}`}
                className={`text-xs px-2 py-1 rounded border transition ${on ? "bg-blue text-white border-blue" : "bg-white text-navy border-tint hover:border-blue"}`}>
                {e.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="block text-sm font-semibold text-navy mb-1">Passos (drip)</label>
        <div className="space-y-2 border border-tint rounded-lg p-3 bg-slate-50/50" data-testid="auto-steps">
          {(automation.steps || []).map((s, i) => (
            <div key={i} className="border border-tint rounded p-2 bg-white" data-testid={`auto-step-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] uppercase bg-navy text-white px-2 py-0.5 rounded">Passo {i + 1}</span>
                <label className="text-xs">Delay (h):</label>
                <input type="number" step="0.5" value={s.delay_hours ?? 0} onChange={(e) => updStep(i, { delay_hours: parseFloat(e.target.value) })}
                  className="w-20 text-xs border border-tint rounded px-2 py-1" data-testid={`auto-delay-${i}`} />
                <span className="text-[11px] text-muted2 flex-1">{i === 0 ? "apos enrolar" : `apos o passo ${i}`}</span>
                <button onClick={() => rmStep(i)} className="text-red-500 p-1"><Trash2 size={12} /></button>
              </div>
              <Field label="Assunto" value={s.assunto || ""} onChange={(v) => updStep(i, { assunto: v })} testid={`auto-assunto-${i}`} />
              <label className="block text-xs font-semibold text-navy mt-2 mb-1">Corpo HTML</label>
              <textarea value={s.corpo_html || ""} onChange={(e) => updStep(i, { corpo_html: e.target.value })}
                data-testid={`auto-html-${i}`} rows={5} className="w-full px-2 py-1 border border-tint rounded text-xs font-mono" />
            </div>
          ))}
          <button onClick={addStep} className="text-xs text-blue font-semibold" data-testid="auto-addstep">+ Adicionar passo</button>
        </div>
      </div>
      <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={automation.ativo !== false} onChange={(e) => upd("ativo", e.target.checked)} data-testid="auto-ativo" /> Ativa</label>
      <div className="flex justify-end gap-2 pt-2">
        <Btn variant="ghost" onClick={onCancel}>Cancelar</Btn>
        <Btn onClick={onSave} testid="auto-save"><Save size={14} /> Guardar</Btn>
      </div>
    </>
  );
}

function ContactPicker({ lists, value, onChange }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filterList, setFilterList] = useState("");
  useEffect(() => {
    const params = {};
    if (filterList) params.list_id = filterList;
    if (q) params.q = q;
    api.get("/marketing/contacts", { params }).then((r) => setItems(r.data || [])).catch(() => {});
  }, [q, filterList]);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <>
      <div className="flex gap-2 mb-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="pesquisar" className="flex-1 text-sm border border-tint rounded px-2 py-1" data-testid="enroll-search" />
        <select value={filterList} onChange={(e) => setFilterList(e.target.value)} className="text-sm border border-tint rounded px-2" data-testid="enroll-list">
          <option value="">Todas as listas</option>
          {lists.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
        </select>
      </div>
      <div className="border border-tint rounded max-h-72 overflow-y-auto" data-testid="enroll-picker">
        {items.length === 0 && <div className="text-center text-muted2 py-6 text-xs">Sem contactos.</div>}
        {items.map((c) => (
          <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 border-b border-tint last:border-b-0 hover:bg-tint text-sm cursor-pointer" data-testid={`enroll-c-${c.id}`}>
            <input type="checkbox" checked={value.includes(c.id)} onChange={() => toggle(c.id)} />
            <span className="flex-1 truncate"><b>{c.nome || c.email}</b> <span className="text-muted2 text-xs">{c.email}</span></span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded ${c.estado === "ativo" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{c.estado}</span>
          </label>
        ))}
      </div>
    </>
  );
}

const HEALTH_META = {
  healthy: { color: "emerald", label: "Saudavel", Icon: CheckCircle2 },
  warning: { color: "amber", label: "Aviso", Icon: AlertTriangle },
  critical: { color: "red", label: "Critico", Icon: AlertTriangle },
  insufficient_data: { color: "slate", label: "Sem dados", Icon: HelpCircle },
};

function HealthPanel({ data, days, onDaysChange, onRefresh }) {
  const [expanded, setExpanded] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [view, setView] = useState("senders");
  const [platforms, setPlatforms] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    if (view === "platforms" && !platforms) {
      api.get(`/marketing/health/platforms?days=${days}`).then((r) => setPlatforms(r.data)).catch(() => {});
    }
    if (view === "alerts") {
      api.get("/marketing/health/alerts?limit=30").then((r) => setAlerts(r.data || [])).catch(() => {});
    }
  }, [view, days]);
  useEffect(() => { setPlatforms(null); }, [days]);

  const runAlerts = async (force = false) => {
    setRunning(true);
    try {
      const { data } = await api.post("/marketing/health/alerts/run", { force, days });
      toast.success(`Verificados: ${data.verificados} - alertados: ${data.alertados} - saltados: ${data.saltados_cooldown}`);
      const { data: h } = await api.get("/marketing/health/alerts?limit=30");
      setAlerts(h || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setRunning(false);
  };
  const openTimeline = async (sid) => {
    if (expanded === sid) { setExpanded(null); setTimeline(null); return; }
    setExpanded(sid);
    try {
      const key = sid || "global";
      const { data } = await api.get(`/marketing/health/senders/${key}/timeline?days=${days}`);
      setTimeline(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!data) return <div className="text-muted2 text-sm">A carregar...</div>;
  const { resumo, senders, thresholds } = data;
  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="text-xs text-muted2 max-w-2xl">
          Saude dos remetentes na janela selecionada. Limites baseados em SendGrid best practices e Gmail Sender Guidelines: bounce &gt; {(thresholds.bounce.warning * 100).toFixed(1)}% avisa, spam &gt; {(thresholds.spam.warning * 100).toFixed(2)}% avisa. Metricas ficam "sem dados" com menos de {thresholds.min_volume_for_rate} envios.
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted2">Periodo:</label>
          <select value={days} onChange={(e) => onDaysChange(parseInt(e.target.value))} data-testid="health-days"
            className="text-xs border border-tint rounded px-2 py-1">
            <option value={7}>7 dias</option>
            <option value={30}>30 dias</option>
            <option value={90}>90 dias</option>
            <option value={0}>Todos</option>
          </select>
          <Btn variant="ghost" onClick={onRefresh} testid="health-refresh"><Play size={13} /> Atualizar</Btn>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-4 border-b border-tint" data-testid="health-subtabs">
        {[["senders", "Por remetente"], ["platforms", "Por plataforma"], ["alerts", "Alertas enviados"], ["digest", "Digest"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`health-sub-${k}`}
            className={`text-xs px-3 py-1.5 border-b-2 transition ${view === k ? "border-blue text-navy font-semibold" : "border-transparent text-muted2 hover:text-navy"}`}>{l}</button>
        ))}
        <div className="flex-1" />
        {view === "alerts" && (
          <>
            <Btn variant="ghost" onClick={() => runAlerts(false)} disabled={running} testid="alerts-run"><Play size={12} /> Verificar agora</Btn>
            <Btn variant="ghost" onClick={() => runAlerts(true)} disabled={running} testid="alerts-force">Forcar envio</Btn>
          </>
        )}
      </div>

      {view === "senders" && (
        <SendersHealthView data={data} openTimeline={openTimeline} expanded={expanded} timeline={timeline} />
      )}

      {view === "platforms" && (
        <PlatformsHealthView data={platforms} thresholds={data.thresholds} />
      )}

      {view === "alerts" && (
        <AlertsHistoryView alerts={alerts} />
      )}

      {view === "digest" && (
        <DigestView days={days} />
      )}
    </div>
  );
}

function SendersHealthView({ data, openTimeline, expanded, timeline }) {
  const { resumo, senders, thresholds } = data;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="health-summary">
        {["critical", "warning", "healthy", "insufficient_data"].map((k) => {
          const m = HEALTH_META[k];
          return (
            <div key={k} className={`border rounded-lg p-3 flex items-center gap-3 ${
              k === "critical" ? "border-red-200 bg-red-50" :
              k === "warning" ? "border-amber-200 bg-amber-50" :
              k === "healthy" ? "border-emerald-200 bg-emerald-50" :
              "border-slate-200 bg-slate-50"
            }`} data-testid={`health-count-${k}`}>
              <m.Icon className={
                k === "critical" ? "text-red-600" :
                k === "warning" ? "text-amber-600" :
                k === "healthy" ? "text-emerald-600" : "text-slate-500"
              } size={22} />
              <div>
                <div className={`text-2xl font-bold ${
                  k === "critical" ? "text-red-700" :
                  k === "warning" ? "text-amber-700" :
                  k === "healthy" ? "text-emerald-700" : "text-slate-700"
                }`}>{resumo[k] || 0}</div>
                <div className="text-[11px] uppercase tracking-wide text-muted2">{m.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="space-y-2" data-testid="health-senders-list">
        {senders.length === 0 && <div className="text-center text-muted2 border border-tint rounded-lg py-12">Sem envios no periodo selecionado.</div>}
        {senders.map((s) => {
          const meta = HEALTH_META[s.health] || HEALTH_META.insufficient_data;
          const sidKey = s.sender_id || "global";
          const isOpen = expanded === s.sender_id;
          const borderCls = s.health === "critical" ? "border-red-300" : s.health === "warning" ? "border-amber-300" : "border-tint";
          const badgeCls = s.health === "critical" ? "bg-red-100 text-red-700" :
                           s.health === "warning" ? "bg-amber-100 text-amber-700" :
                           s.health === "healthy" ? "bg-emerald-100 text-emerald-700" :
                           "bg-slate-100 text-slate-600";
          const iconCls = s.health === "critical" ? "text-red-600" :
                          s.health === "warning" ? "text-amber-600" :
                          s.health === "healthy" ? "text-emerald-600" : "text-slate-500";
          return (
            <div key={sidKey} data-testid={`health-sender-${sidKey}`}
              className={`border rounded-lg bg-white ${borderCls}`}>
              <div className="p-3 flex items-start gap-3">
                <meta.Icon className={`mt-1 ${iconCls}`} size={20} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-navy truncate">{s.sender_nome}</span>
                    <span className="text-[11px] text-muted2 truncate">{s.sender_email || "-"}</span>
                    {s.plataforma && <span className="text-[9px] bg-tint text-navy px-1.5 py-0.5 rounded">{s.plataforma}</span>}
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${badgeCls}`}>{meta.label}</span>
                  </div>
                  <div className="grid grid-cols-6 gap-2 mt-3 text-center">
                    <Stat label="Enviados" value={s.totais.enviados} />
                    <Stat label="Entrega" value={pct(s.taxas.delivery)} bad={s.totais.enviados >= 20 && s.taxas.delivery < thresholds.delivery.warning} good={s.totais.enviados >= 20 && s.taxas.delivery >= thresholds.delivery.warning} />
                    <Stat label="Aberturas" value={pct(s.taxas.open)} />
                    <Stat label="Cliques" value={pct(s.taxas.click)} />
                    <Stat label="Bounce" value={pct(s.taxas.bounce)} bad={s.taxas.bounce >= thresholds.bounce.warning} />
                    <Stat label="Spam" value={pct(s.taxas.spam)} bad={s.taxas.spam >= thresholds.spam.warning} />
                  </div>
                  {s.alerts.length > 0 && (
                    <div className="mt-3 space-y-1" data-testid={`health-alerts-${sidKey}`}>
                      {s.alerts.map((a, i) => (
                        <div key={i} className={`text-xs rounded px-2 py-1 flex gap-2 items-start ${a.level === "critical" ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-800"}`}>
                          <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                          <span>{a.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => openTimeline(s.sender_id)} data-testid={`health-timeline-${sidKey}`}
                  className="text-[11px] px-2 py-1 rounded border border-tint hover:bg-tint">
                  {isOpen ? "Fechar" : "Timeline"}
                </button>
              </div>
              {isOpen && timeline && (
                <div className="border-t border-tint px-3 py-2 bg-slate-50" data-testid={`health-timeline-panel-${sidKey}`}>
                  {timeline.series.length === 0 ? (
                    <div className="text-xs text-muted2 py-2 text-center">Sem dados diarios.</div>
                  ) : (
                    <TimelineChart series={timeline.series} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function Stat({ label, value, good, bad }) {
  const cls = bad ? "text-red-600" : good ? "text-emerald-700" : "text-navy";
  return (
    <div>
      <div className={`text-sm font-bold ${cls}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wide text-muted2">{label}</div>
    </div>
  );
}

function TimelineChart({ series }) {
  const maxV = Math.max(1, ...series.map((s) => s.enviados));
  return (
    <div className="flex items-end gap-1 h-24 mt-1" data-testid="health-tl-bars">
      {series.map((s) => {
        const h = Math.round((s.enviados / maxV) * 100);
        const bad = s.bounces + s.spam;
        const badPct = s.enviados > 0 ? (bad / s.enviados) * 100 : 0;
        const barCls = badPct > 3 ? "bg-red-500" : badPct > 1 ? "bg-amber-500" : "bg-blue";
        return (
          <div key={s.dia} title={`${s.dia} - env:${s.enviados} bnc:${s.bounces} spam:${s.spam} unsub:${s.unsubs}`}
            className="flex-1 flex flex-col items-center gap-0.5 min-w-[8px]">
            <div className={`w-full rounded-sm ${barCls}`}
              style={{ height: `${h}%`, minHeight: "2px" }} />
            <div className="text-[7px] text-muted2 rotate-45 origin-left">{s.dia.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}

function pct(v) {
  if (v === null || v === undefined) return "-";
  return `${(v * 100).toFixed(v >= 0.1 ? 1 : 2)}%`;
}

function PlatformsHealthView({ data, thresholds }) {
  if (!data) return <div className="text-muted2 text-sm py-8 text-center">A carregar...</div>;
  const plats = data.platforms || [];
  if (plats.length === 0) return <div className="text-center text-muted2 border border-tint rounded-lg py-12">Sem envios registados por plataforma.</div>;
  const maxEnviados = Math.max(1, ...plats.map((p) => p.totais.enviados));
  return (
    <div className="space-y-3" data-testid="platforms-view">
      <div className="text-[11px] text-muted2">
        Comparacao lado-a-lado das taxas por marca/plataforma. Barras a vermelho indicam a taxa acima do critico, a amarelo indicam aviso.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-tint rounded-lg overflow-hidden" data-testid="platforms-table">
          <thead className="bg-tint text-navy text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Plataforma</th>
              <th className="text-center px-2 py-2">Estado</th>
              <th className="text-right px-2 py-2">Volume</th>
              <th className="text-right px-2 py-2">Entrega</th>
              <th className="text-right px-2 py-2">Aberturas</th>
              <th className="text-right px-2 py-2">Cliques</th>
              <th className="text-right px-2 py-2">Bounce</th>
              <th className="text-right px-2 py-2">Spam</th>
              <th className="text-right px-2 py-2">Unsub</th>
              <th className="text-center px-2 py-2">Remetentes</th>
            </tr>
          </thead>
          <tbody>
            {plats.map((p) => {
              const meta = HEALTH_META[p.health] || HEALTH_META.insufficient_data;
              const volBar = Math.round((p.totais.enviados / maxEnviados) * 100);
              return (
                <tr key={p.plataforma} className="border-t border-tint" data-testid={`plat-row-${p.plataforma}`}>
                  <td className="px-3 py-2 font-semibold text-navy">{p.plataforma}</td>
                  <td className="px-2 py-2 text-center">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-bold ${
                      p.health === "critical" ? "bg-red-100 text-red-700" :
                      p.health === "warning" ? "bg-amber-100 text-amber-700" :
                      p.health === "healthy" ? "bg-emerald-100 text-emerald-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>{meta.label}</span>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <div className="text-xs font-semibold text-navy">{p.totais.enviados}</div>
                    <div className="w-full h-1 bg-slate-100 rounded overflow-hidden mt-0.5">
                      <div className="h-full bg-blue" style={{ width: `${volBar}%` }} />
                    </div>
                  </td>
                  <TxCell v={p.taxas.delivery} th={thresholds.delivery} lowerBad />
                  <TxCell v={p.taxas.open} />
                  <TxCell v={p.taxas.click} />
                  <TxCell v={p.taxas.bounce} th={thresholds.bounce} />
                  <TxCell v={p.taxas.spam} th={thresholds.spam} />
                  <TxCell v={p.taxas.unsub} th={thresholds.unsub} />
                  <td className="px-2 py-2 text-center text-[10px] text-muted2">{(p.remetentes || []).length}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TxCell({ v, th, lowerBad }) {
  let color = "text-navy";
  if (th) {
    if (lowerBad) {
      if (v > 0 && v <= th.critical) color = "text-red-600 font-bold";
      else if (v > 0 && v <= th.warning) color = "text-amber-600";
    } else {
      if (v >= th.critical) color = "text-red-600 font-bold";
      else if (v >= th.warning) color = "text-amber-600";
    }
  }
  return <td className={`px-2 py-2 text-right text-xs ${color}`}>{pct(v)}</td>;
}

function AlertsHistoryView({ alerts }) {
  if (!alerts || alerts.length === 0) return (
    <div className="text-center text-muted2 border border-tint rounded-lg py-12" data-testid="alerts-empty">
      Sem alertas enviados ainda. Clica em "Verificar agora" para correr a analise.
    </div>
  );
  return (
    <div className="space-y-2" data-testid="alerts-list">
      {alerts.map((a) => (
        <div key={a.id} data-testid={`alert-${a.id}`}
          className="border border-red-200 bg-red-50 rounded-lg p-3 flex items-start gap-3">
          <AlertTriangle className="text-red-600 mt-0.5" size={18} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-navy">{a.sender_nome}</span>
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${a.entregue ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                {a.entregue ? "email entregue" : "envio falhou"}
              </span>
              <span className="text-[10px] text-muted2">{fmtDT(a.enviado_em)}</span>
            </div>
            <div className="text-[11px] text-muted2 mt-1">
              Para: {(a.recipients || []).join(", ") || "-"}
            </div>
            <div className="grid grid-cols-4 gap-2 mt-2 text-center">
              <MicroStat label="bounce" v={a.metrics_snapshot?.taxas?.bounce} />
              <MicroStat label="spam" v={a.metrics_snapshot?.taxas?.spam} />
              <MicroStat label="unsub" v={a.metrics_snapshot?.taxas?.unsub} />
              <MicroStat label="entrega" v={a.metrics_snapshot?.taxas?.delivery} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MicroStat({ label, v }) {
  return (
    <div>
      <div className="text-sm font-bold text-navy">{pct(v)}</div>
      <div className="text-[9px] uppercase text-muted2">{label}</div>
    </div>
  );
}


function DigestView({ days }) {
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [previewDays, setPreviewDays] = useState(7);
  const [running, setRunning] = useState(false);
  const load = async () => {
    try {
      const [p, h] = await Promise.all([
        api.get(`/marketing/health/digest/preview?days=${previewDays}`),
        api.get("/marketing/health/digest/history?limit=15"),
      ]);
      setPreview(p.data);
      setHistory(h.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [previewDays]);
  const enviarAgora = async (force) => {
    setRunning(true);
    try {
      const { data } = await api.post("/marketing/health/digest/run", { force, days: previewDays });
      if (!data.enviado) {
        toast.message(`Nao enviado - motivo: ${data.motivo}`);
      } else {
        toast.success(`Digest enviado para ${data.recipients.length} destinatario(s)`);
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setRunning(false);
  };
  if (!preview) return <div className="text-muted2 text-sm py-8 text-center">A carregar...</div>;
  return (
    <div className="space-y-4" data-testid="digest-view">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs text-muted2">Janela do preview:</label>
        <select value={previewDays} onChange={(e) => setPreviewDays(parseInt(e.target.value))}
          data-testid="digest-days" className="text-xs border border-tint rounded px-2 py-1">
          <option value={1}>1 dia</option>
          <option value={7}>7 dias</option>
          <option value={30}>30 dias</option>
        </select>
        <div className="flex-1" />
        <Btn variant="ghost" onClick={() => enviarAgora(false)} disabled={running} testid="digest-run"><Send size={12} /> Enviar agora</Btn>
        <Btn variant="ghost" onClick={() => enviarAgora(true)} disabled={running} testid="digest-force">Forcar envio</Btn>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="digest-globals">
        <MicroKPI label="Envios" value={preview.globais.envios} />
        <MicroKPI label="Entrega" value={pct(preview.globais.taxa_entrega)} good={preview.globais.taxa_entrega >= 0.95} />
        <MicroKPI label="Bounce" value={pct(preview.globais.taxa_bounce)} bad={preview.globais.taxa_bounce >= 0.02} />
        <MicroKPI label="Spam" value={pct(preview.globais.taxa_spam)} bad={preview.globais.taxa_spam >= 0.001} />
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-blue font-semibold mb-2">Bounce/spam por plataforma</div>
        <div className="border border-tint rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-tint text-navy uppercase text-[10px]"><tr>
              <th className="text-left px-2 py-1.5">Plataforma</th>
              <th className="text-right px-2 py-1.5">Envios</th>
              <th className="text-right px-2 py-1.5">Bounce</th>
              <th className="text-right px-2 py-1.5">Spam</th>
              <th className="text-center px-2 py-1.5">Estado</th>
            </tr></thead>
            <tbody>
              {preview.plataformas.length === 0 && <tr><td colSpan={5} className="text-center text-muted2 py-3">Sem dados</td></tr>}
              {preview.plataformas.map((p) => (
                <tr key={p.plataforma} className="border-t border-tint" data-testid={`digest-plat-${p.plataforma}`}>
                  <td className="px-2 py-1.5 font-semibold text-navy">{p.plataforma}</td>
                  <td className="px-2 py-1.5 text-right">{p.envios}</td>
                  <td className={`px-2 py-1.5 text-right ${p.bounce >= 0.02 ? "text-red-600 font-bold" : ""}`}>{pct(p.bounce)}</td>
                  <td className={`px-2 py-1.5 text-right ${p.spam >= 0.001 ? "text-red-600 font-bold" : ""}`}>{pct(p.spam)}</td>
                  <td className="px-2 py-1.5 text-center">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                      p.health === "critical" ? "bg-red-100 text-red-700" :
                      p.health === "warning" ? "bg-amber-100 text-amber-700" :
                      p.health === "healthy" ? "bg-emerald-100 text-emerald-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>{HEALTH_META[p.health]?.label || p.health}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-blue font-semibold mb-2">Top 5 campanhas com pior desempenho</div>
        <div className="border border-tint rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-tint text-navy uppercase text-[10px]"><tr>
              <th className="text-left px-2 py-1.5">Campanha</th>
              <th className="text-right px-2 py-1.5">Envios</th>
              <th className="text-right px-2 py-1.5">Bounce</th>
              <th className="text-right px-2 py-1.5">Spam</th>
              <th className="text-right px-2 py-1.5">Abertura</th>
            </tr></thead>
            <tbody>
              {preview.pior_campanhas.length === 0 && <tr><td colSpan={5} className="text-center text-muted2 py-3">Sem campanhas com volume suficiente</td></tr>}
              {preview.pior_campanhas.map((c) => (
                <tr key={c.id} className="border-t border-tint">
                  <td className="px-2 py-1.5">
                    <div className="font-semibold text-navy">{c.nome}</div>
                    <div className="text-[10px] text-muted2 truncate">{c.plataforma} - {c.assunto}</div>
                  </td>
                  <td className="px-2 py-1.5 text-right">{c.envios}</td>
                  <td className={`px-2 py-1.5 text-right ${c.bounce >= 0.02 ? "text-red-600 font-bold" : ""}`}>{pct(c.bounce)}</td>
                  <td className={`px-2 py-1.5 text-right ${c.spam >= 0.001 ? "text-red-600 font-bold" : ""}`}>{pct(c.spam)}</td>
                  <td className="px-2 py-1.5 text-right">{pct(c.abertura)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-blue font-semibold mb-2">Historico ({history.length})</div>
        <div className="space-y-1" data-testid="digest-history">
          {history.length === 0 && <div className="text-xs text-muted2 border border-tint rounded p-3 text-center">Sem digests enviados ainda.</div>}
          {history.map((h) => (
            <div key={h.id} className="border border-tint rounded p-2 flex items-center gap-2 text-xs">
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${h.entregue ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                {h.entregue ? "entregue" : "falhou"}
              </span>
              <span className="font-semibold text-navy">{h.dia}</span>
              <span className="text-muted2">{h.frequencia} - {h.periodo_dias}d</span>
              <span className="text-muted2 truncate flex-1">para {(h.recipients || []).join(", ")}</span>
              <span className="text-navy">{h.resumo?.globais?.envios || 0} envios</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MicroKPI({ label, value, good, bad }) {
  const cls = bad ? "text-red-600" : good ? "text-emerald-700" : "text-navy";
  return (
    <div className="border border-tint rounded-lg p-2 bg-white">
      <div className={`text-lg font-bold ${cls}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wide text-muted2">{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard nativo (Iter102) - substituto de PostHog: grafico + tabela ordenavel
// ---------------------------------------------------------------------------
function MarketingDashboard({ analytics, campaigns, nav }) {
  const [days, setDays] = useState(30);
  const [series, setSeries] = useState([]);
  const [top, setTop] = useState([]);
  const [sortKey, setSortKey] = useState("enviada_em");
  const [sortDir, setSortDir] = useState("desc"); // asc | desc
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/marketing/analytics/timeseries?days=${days}`),
      api.get(`/marketing/analytics/top-campaigns?limit=50`),
    ]).then(([ts, tc]) => {
      if (!alive) return;
      setSeries((ts.data?.series || []).map((r) => ({
        data: r.data.slice(5), // MM-DD
        Enviados: r.enviados, Aberturas: r.opens, Cliques: r.clicks,
      })));
      setTop(tc.data?.campaigns || []);
    }).catch(() => { setSeries([]); setTop([]); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [days]);

  const sorted = useMemo(() => {
    const rows = [...top];
    rows.sort((a, b) => {
      const va = a[sortKey] ?? 0, vb = b[sortKey] ?? 0;
      if (typeof va === "string" || typeof vb === "string") {
        return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
      }
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return rows;
  }, [top, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  const totalSent = series.reduce((s, r) => s + (r.Enviados || 0), 0);
  const totalOpens = series.reduce((s, r) => s + (r.Aberturas || 0), 0);
  const totalClicks = series.reduce((s, r) => s + (r.Cliques || 0), 0);

  return (
    <div data-testid="mkt-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="mkt-analytics">
        {[
          ["Enviados", analytics.enviados, "text-navy"],
          ["Aberturas", `${analytics.opens} (${analytics.open_rate}%)`, "text-blue"],
          ["Cliques", `${analytics.clicks} (${analytics.click_rate}%)`, "text-emerald-600"],
          ["Bounces", analytics.bounces, "text-red-600"],
          ["Unsubs", analytics.unsubs, "text-amber-600"],
        ].map(([k, v, cls]) => (
          <div key={k} className="border border-tint rounded-lg p-3 bg-white">
            <div className="text-[10px] uppercase tracking-wider text-muted2">{k}</div>
            <div className={`text-2xl font-bold ${cls}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="border border-tint rounded-lg bg-white p-3 mb-4" data-testid="mkt-timeseries">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted2">Serie diaria</div>
            <div className="text-[11px] text-muted2">
              {totalSent} envios, {totalOpens} aberturas, {totalClicks} cliques nos ultimos {days} dias
            </div>
          </div>
          <div className="flex items-center gap-1" data-testid="mkt-timeseries-range">
            {[7, 14, 30, 60, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`ts-days-${d}`}
                className={`text-[11px] px-2 py-1 rounded border ${days === d ? "bg-navy text-white border-navy" : "border-tint text-slate-600 hover:bg-tint"}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
        <div style={{ width: "100%", height: 240 }}>
          {loading ? (
            <div className="h-full flex items-center justify-center text-muted2 text-sm">A carregar...</div>
          ) : series.length === 0 ? (
            <div className="h-full flex items-center justify-center text-muted2 text-sm">Sem dados no periodo.</div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={series} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="data" tick={{ fontSize: 10, fill: "#64748b" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Enviados" fill="#0f172a" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Aberturas" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Cliques" fill="#10b981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="border border-tint rounded-lg bg-white overflow-hidden" data-testid="mkt-top-table">
        <div className="px-3 py-2 border-b border-tint flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted2">Performance de campanhas</div>
            <div className="text-[11px] text-muted2">Ordenavel. Clica no cabecalho para mudar criterio.</div>
          </div>
          <span className="text-[11px] text-muted2">{sorted.length} campanhas enviadas</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-tint/40 text-navy">
              <tr>
                {[
                  ["nome", "Campanha", "text-left"],
                  ["plataforma", "Plataforma", "text-left"],
                  ["enviada_em", "Enviada em", "text-left"],
                  ["enviados", "Env", "text-right"],
                  ["opens", "Aberturas", "text-right"],
                  ["open_rate", "% Abrir", "text-right"],
                  ["clicks", "Cliques", "text-right"],
                  ["click_rate", "% Clicar", "text-right"],
                  ["bounces", "Bounces", "text-right"],
                  ["unsubs", "Unsubs", "text-right"],
                ].map(([k, label, align]) => (
                  <th key={k} onClick={() => toggleSort(k)} data-testid={`col-${k}`}
                    className={`px-2 py-2 font-semibold cursor-pointer select-none whitespace-nowrap ${align} hover:bg-tint`}>
                    <span className="inline-flex items-center gap-1">
                      {label}
                      <ArrowUpDown size={10} className={sortKey === k ? "text-blue" : "text-slate-300"} />
                      {sortKey === k && <span className="text-[9px] text-blue">{sortDir === "asc" ? "^" : "v"}</span>}
                    </span>
                  </th>
                ))}
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="px-3 py-6 text-center text-muted2">A carregar...</td></tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-6 text-center text-muted2">Sem campanhas enviadas ainda.</td></tr>
              )}
              {!loading && sorted.map((r) => (
                <tr key={r.id} data-testid={`row-campaign-${r.id}`} className="border-t border-tint hover:bg-tint/30">
                  <td className="px-2 py-1.5">
                    <div className="font-semibold text-navy truncate max-w-[280px]">{r.nome}</div>
                    <div className="text-[10px] text-muted2 truncate max-w-[280px]">{r.assunto}</div>
                  </td>
                  <td className="px-2 py-1.5 text-slate-600">{r.plataforma || "-"}</td>
                  <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">
                    {r.enviada_em ? fmtD(r.enviada_em) : "-"}
                    {r.ab && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-indigo-100 text-indigo-700 font-bold">A/B</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.enviados}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.opens}</td>
                  <td className="px-2 py-1.5 text-right font-bold text-blue">{r.open_rate}%</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.clicks}</td>
                  <td className="px-2 py-1.5 text-right font-bold text-emerald-600">{r.click_rate}%</td>
                  <td className="px-2 py-1.5 text-right text-red-500">{r.bounces}</td>
                  <td className="px-2 py-1.5 text-right text-amber-600">{r.unsubs}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button onClick={() => nav(`/app/marketing/editor/${r.id}`)} data-testid={`open-${r.id}`}
                      className="text-[11px] px-2 py-0.5 rounded border border-blue text-blue hover:bg-blue/5">
                      Abrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
